import { useEffect, useState } from "react";

import useAxios from "@hooks/use-axios";
import { OPS_ENDPOINTS } from "@constants/endpoints";
import { defaultDisbursementFilters, toDisbursementFilterParams } from "../misc/schema";

const DEFAULT_LIMIT = 20;
// The debit note search fires on every keystroke — wait for typing to settle
// before re-fetching so each character doesn't send its own request.
const FILTER_DEBOUNCE_MS = 400;

const useDisbursementQueue = () => {
    const { axiosInstance, loading } = useAxios({ base: "ops", initialLoader: true });
    const [items, setItems] = useState([]);
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: DEFAULT_LIMIT });
    const [utrTarget, setUtrTarget] = useState(null);
    const [revertTarget, setRevertTarget] = useState(null);
    const [filters, setFilters] = useState(defaultDisbursementFilters);

    const fetchQueue = async (page = 1, limit = DEFAULT_LIMIT, currentFilters = filters) => {
        const params = await toDisbursementFilterParams(currentFilters);
        try {
            const res = await axiosInstance.get(OPS_ENDPOINTS.DISBURSEMENTS, { params: { page, limit, ...params } });
            const result = res.data || res;
            const list = result.items || result.data || result.results || [];
            setItems(list);
            setPagination({
                total: result.total ?? list.length,
                page : result.page ?? page,
                limit: result.limit ?? limit
            });
        } catch {
            setItems([]);
            setPagination({ total: 0, page, limit });
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => fetchQueue(1, pagination.limit, filters), FILTER_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePageChange = (limit, page) => fetchQueue(page, limit, filters);
    const handlePerRowsChange = (limit) => fetchQueue(1, limit, filters);

    const handleFilterChange = (nextFilters) => {
        // The [filters] effect above re-fetches from page 1 with the merged value.
        setFilters((prev) => ({ ...prev, ...nextFilters }));
    };

    const openUtrModal = (invoice) => setUtrTarget(invoice);
    const closeUtrModal = () => setUtrTarget(null);
    const openRevertModal = (invoice) => setRevertTarget(invoice);
    const closeRevertModal = () => setRevertTarget(null);

    const uploadUTR = async (payload) => {
        await axiosInstance.patch(OPS_ENDPOINTS.MARK_INVOICE_PAID(utrTarget._id), {
            payment_reference: payload.utr,
            payment_date     : payload.payment_date,
            payment_mode     : payload.mode
        });
        await fetchQueue(pagination.page, pagination.limit);
    };

    const revertPayment = async (reason) => {
        await axiosInstance.patch(OPS_ENDPOINTS.REVERT_INVOICE_PAYMENT(revertTarget._id), { reason });
        await fetchQueue(pagination.page, pagination.limit);
    };

    return {
        items,
        pagination,
        loading,
        filters,
        handleFilterChange,
        handlePageChange,
        handlePerRowsChange,
        utrTarget,
        revertTarget,
        openUtrModal,
        closeUtrModal,
        openRevertModal,
        closeRevertModal,
        uploadUTR,
        revertPayment
    };
};

export default useDisbursementQueue;
